import * as React from 'react'
import { IconTypes } from '../Icons/Icon'
import ERoute from '../../ERoute'
import Fuse from 'fuse.js'
import SearchController from '../../controllers/SearchController'
import Highlighter from '../../utilities/Highlighter'
import SearchItem from './SearchItem'
import { AppState } from '../../store'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router'
import RouteHelper from '../../helpers/RouteHelper'
import SearchLoader from './SearchLoader'
import styled from 'styled-components'
import { Style } from '../../styles'
import { withTranslation, WithTranslation } from 'react-i18next'
import UserWorkspaceSettingHelper from '../../helpers/UserWorkspaceSettingHelper'
import { CurrentUser, Task, UserWorkspaceSettingRole, UserWorkspaceSettingScope } from '../../types'
import Utils from '../../utilities/Utils'
import { showTaskModal } from '../../store/modals/actions'
import { AnyAction, Dispatch } from 'redux'
import { Command, CommandGroup, CommandInput, CommandList } from '@/components/ui/command'
import SHORTCUT_ICONS from './shortcutIcons'

const Wrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		width: 100%;
	}
`

const ResultsPositioner = styled.div`
	position: absolute;
	right: 0;
	width: 100%;
	min-width: 400px;
	top: 38px;
	z-index: 9;

	&.is-centered {
		right: initial;
		left: 50%;
		transform: translateX(-50%);
	}

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		top: 28px;
		left: 0;
		right: 0;
		transform: none;
		min-width: initial;
	}
`

interface IDispatchToProps {
  showTaskModal: typeof showTaskModal
}

type IProps = RouteComponentProps<any> & {
  currentUser: CurrentUser
  centered?: boolean
} & IDispatchToProps & IStateToProps & WithTranslation

interface IState {
  value: string,
  didFetch: boolean,
  isLoading: boolean,
  results: any[] | null
  shortcuts: IShortcut[]
}

interface IShortcut {
  label: string
  object: string
  link: string
  icon: IconTypes
  visible: boolean
}

class Search extends React.PureComponent<IProps, IState> {
  private container = React.createRef<HTMLDivElement>()
  private shortcutFuzzySearch: any

  static defaultProps = {
    centered: false
  }

  constructor(props: IProps) {
    super(props);

    this.state = {
      value: '',
      didFetch: false,
      isLoading: false,
      results: null,
      shortcuts: [],
    };

    this.shortcutFuzzySearch = new Fuse(this.getShortCuts(), {
      shouldSort: true,
      threshold: 0.3,
      location: 0,
      distance: 100,
      minMatchCharLength: 1,
      keys: [
        'label',
        'link',
        'object',
      ]
    });

    this.getSearchResults = Utils.debounce(this.getSearchResults, 150, false)
    this.resetForm = this.resetForm.bind(this)
    this.onSearchInputChange = this.onSearchInputChange.bind(this);
    this.onInputKeyDown = this.onInputKeyDown.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onClickOutsideComponent = this.onClickOutsideComponent.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onClickOutsideComponent);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onClickOutsideComponent);
  }

  onClickOutsideComponent(e) {
    if (this.container.current && !this.container.current.contains(e.target)) {
      this.resetForm();
    }
  }

  getShortCuts() {
    const { t, currentUser: { workspace: { setting } } } = this.props

    const SHORTCUTS: IShortcut[] = [
      { label: t('Search::Dashboard'), object: 'shortcut', link: ERoute.PATH_DASHBOARD, icon: 'dashboard-navigation', visible: true },
      { label: t('Search::Calendar'), object: 'shortcut', link: ERoute.PATH_CALENDAR, icon: 'calendar-day', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.CALENDAR) },
      { label: t('Search::Scheduling › Event types'), object: 'shortcut', link: ERoute.PATH_SCHEDULING_EVENT_TYPES, icon: 'calendar-clock', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BOOKING) },
      { label: t('Search::Scheduling › Bookings'), object: 'shortcut', link: ERoute.PATH_SCHEDULING_BOOKINGS, icon: 'calendar-check', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BOOKING) },
      { label: t('Search::Tasks'), object: 'shortcut', link: ERoute.PATH_TASKS_OVERVIEW, icon: 'tasks', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TASK) },
      { label: t('Search::Tasks › My tasks'), object: 'shortcut', link: ERoute.PATH_TASKS_MY_TASKS, icon: 'tasks', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TASK) },
      { label: t('Search::Tasks › Today'), object: 'shortcut', link: ERoute.PATH_TASKS_TODAY, icon: 'tasks', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TASK) },
      { label: t('Search::Tasks › Upcoming'), object: 'shortcut', link: ERoute.PATH_TASKS_UPCOMING, icon: 'tasks', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TASK) },
      { label: t('Search::Tasks › Planner'), object: 'shortcut', link: ERoute.PATH_TASKS_PLANNER, icon: 'tasks', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TASK) },
      { label: t('Search::Contacts'), object: 'shortcut', link: ERoute.PATH_CONTACTS, icon: 'user', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.CONTACT) },
      { label: t('Search::Deals'), object: 'shortcut', link: ERoute.PATH_DEALS, icon: 'check-circle', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.DEAL) },
      { label: t('Search::Sequences'), object: 'shortcut', link: ERoute.PATH_SEQUENCES, icon: 'paper-plane', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SEQUENCE) },
      { label: t('Search::Projects'), object: 'shortcut', link: ERoute.PATH_PROJECTS, icon: 'project-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROJECT) },
      { label: t('Search::Projects › Active'), object: 'shortcut', link: ERoute.PATH_PROJECTS_ACTIVE, icon: 'project-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROJECT) },
      { label: t('Search::Projects › Proposal'), object: 'shortcut', link: ERoute.PATH_PROJECTS_PROPOSAL, icon: 'project-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROJECT) },
      { label: t('Search::Projects › Finished'), object: 'shortcut', link: ERoute.PATH_PROJECTS_COMPLETED, icon: 'project-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROJECT) },
      { label: t('Search::Projects › Cancelled'), object: 'shortcut', link: ERoute.PATH_PROJECTS_CANCELLED, icon: 'project-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROJECT) },
      { label: t('Search::Timesheets'), object: 'shortcut', link: ERoute.PATH_TIMESHEETS, icon: 'stopwatch', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.TIME_TRACKING) },
      { label: t('Search::Proposals'), object: 'shortcut', link: ERoute.PATH_PROPOSALS, icon: 'proposal', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PROPOSAL) },
      { label: t('Search::Playbooks'), object: 'shortcut', link: ERoute.PATH_PLAYBOOKS, icon: 'book', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PLAYBOOK) },
      { label: t('Search::Contracts'), object: 'shortcut', link: ERoute.PATH_CONTRACTS, icon: 'file-contract', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.CONTRACT) },
      { label: t('Search::Documents › Order forms'), object: 'shortcut', link: ERoute.PATH_ORDER_FORMS, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) && setting.order_forms_enabled },
      { label: t('Search::Documents › Delivery notes'), object: 'shortcut', link: ERoute.PATH_DELIVERY_NOTES, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) && setting.delivery_notes_enabled },
      { label: t('Search::Documents › Quotations'), object: 'shortcut', link: ERoute.PATH_QUOTATIONS, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) },
      { label: t('Search::Documents › Proforma invoices'), object: 'shortcut', link: ERoute.PATH_PRO_FORMA_INVOICES, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) && setting.pro_forma_invoices_enabled },
      { label: t('Search::Documents › Invoices'), object: 'shortcut', link: ERoute.PATH_INVOICES, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) },
      { label: t('Search::Documents › Creditnotes'), object: 'shortcut', link: ERoute.PATH_CREDIT_NOTES, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) },
      { label: t('Search::Documents › Recurring Invoices'), object: 'shortcut', link: ERoute.PATH_RECURRING_INVOICES, icon: 'invoice', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) },
      { label: t('Search::Documents › Invoice conditions'), object: 'shortcut', link: ERoute.PATH_LEDGER_CONDITIONS, icon: 'expense', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INVOICE) },
      { label: t('Search::Expenses'), object: 'shortcut', link: ERoute.PATH_EXPENSES, icon: 'receipt', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.EXPENSE) },
      { label: t('Search::Inbox'), object: 'shortcut', link: ERoute.PATH_INBOX, icon: 'inbox', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INBOX) },
      { label: t('Search::Inbox › All'), object: 'shortcut', link: ERoute.PATH_INBOX_ALL, icon: 'inbox', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INBOX) },
      { label: t('Search::Inbox › Unassigned'), object: 'shortcut', link: ERoute.PATH_INBOX_UNASSIGNED, icon: 'inbox', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.INBOX) },
      { label: t('Search::Help centers'), object: 'shortcut', link: ERoute.PATH_HELP_CENTERS, icon: 'book', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.HELP_CENTER) },
      { label: t('Search::News centers'), object: 'shortcut', link: ERoute.PATH_NEWS_CENTERS, icon: 'bullhorn', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.NEWS_CENTER) },
      { label: t('Search::Widgets'), object: 'shortcut', link: ERoute.PATH_WIDGETS, icon: 'messenger-conversations-active', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.WIDGET) },
      { label: t('Search::Bank'), object: 'shortcut', link: ERoute.PATH_BANK, icon: 'bank', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BANK) },
      { label: t('Search::Bank › Accounts'), object: 'shortcut', link: ERoute.PATH_BANK_ACCOUNTS, icon: 'sack-dollar', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BANK) },
      { label: t('Search::Bank › Transactions'), object: 'shortcut', link: ERoute.PATH_BANK_TRANSACTIONS, icon: 'arrow-right-arrow-left', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BANK) },
      { label: t('Search::Bank › Payments'), object: 'shortcut', link: ERoute.PATH_BANK_PAYMENTS, icon: 'money-bill', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.BANK_PAYMENT) },
      { label: t('Search::Products'), object: 'shortcut', link: ERoute.PATH_PRODUCTS, icon: 'product-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.PRODUCT) },
      { label: t('Search::Reporting'), object: 'shortcut', link: ERoute.PATH_REPORTING, icon: 'reporting-navigation', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.REPORTING) },
      { label: t('Search::Integrations'), object: 'shortcut', link: ERoute.PATH_INTEGRATIONS, icon: 'bolt', visible: UserWorkspaceSettingHelper.hasRole(UserWorkspaceSettingRole.OWNER) },
      { label: t('Search::Account › Profile'), object: 'shortcut', link: ERoute.PATH_ACCOUNT_PROFILE, icon: 'user', visible: true },
      { label: t('Search::Account › Subscription'), object: 'shortcut', link: ERoute.PATH_ACCOUNT_SUBSCRIPTION, icon: 'creditcard', visible: UserWorkspaceSettingHelper.hasRole(UserWorkspaceSettingRole.OWNER) },
      { label: t('Search::Settings'), object: 'shortcut', link: ERoute.PATH_SETTINGS, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › General'), object: 'shortcut', link: ERoute.PATH_SETTINGS_GENERAL, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Company details'), object: 'shortcut', link: ERoute.PATH_SETTINGS_COMPANY, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Company branding'), object: 'shortcut', link: ERoute.PATH_SETTINGS_COMPANY_BRANDING, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Time settings'), object: 'shortcut', link: ERoute.PATH_SETTINGS_DISPLAY_PREFERENCES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Calendar'), object: 'shortcut', link: ERoute.PATH_SETTINGS_CALENDAR, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Order forms'), object: 'shortcut', link: ERoute.PATH_SETTINGS_ORDER_FORMS, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Delivery notes'), object: 'shortcut', link: ERoute.PATH_SETTINGS_DELIVERY_NOTES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Quotations'), object: 'shortcut', link: ERoute.PATH_SETTINGS_QUOTATIONS, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Proforma invoices'), object: 'shortcut', link: ERoute.PATH_SETTINGS_PRO_FORMA_INVOICES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Invoices'), object: 'shortcut', link: ERoute.PATH_SETTINGS_INVOICES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Creditnotes'), object: 'shortcut', link: ERoute.PATH_SETTINGS_CREDIT_NOTES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Email'), object: 'shortcut', link: ERoute.PATH_SETTINGS_EMAIL, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Menu editor'), object: 'shortcut', link: ERoute.PATH_SETTINGS_MENU_EDITOR, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Custom SMTP'), object: 'shortcut', link: ERoute.PATH_SETTINGS_SMTP, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Backup'), object: 'shortcut', link: ERoute.PATH_SETTINGS_DATA, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Settings › Work types'), object: 'shortcut', link: ERoute.PATH_SETTINGS_WORK_TYPES, icon: 'setting', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) },
      { label: t('Search::Team'), object: 'shortcut', link: ERoute.PATH_TEAM, icon: 'team', visible: UserWorkspaceSettingHelper.hasRole(UserWorkspaceSettingRole.OWNER) },
      { label: t('Search::Forms'), object: 'shortcut', link: ERoute.PATH_FORMS, icon: 'input-pipe', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.FORM) },
      { label: t('Search::Files'), object: 'shortcut', link: ERoute.PATH_FILES, icon: 'folder', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.FILE) },
      { label: t('Search::Accountant › Send'), object: 'shortcut', link: ERoute.PATH_ACCOUNTANT_SEND, icon: 'accountant', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.ACCOUNTANT) },
      { label: t('Search::Accountant › Documents'), object: 'shortcut', link: ERoute.PATH_ACCOUNTANT_DOCUMENTS, icon: 'copy', visible: UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.DOCUMENT) },
      { label: t('Search::Referral program'), object: 'shortcut', link: ERoute.PATH_REFER_A_FRIEND, icon: 'check-circle', visible: true },
    ]

    return SHORTCUTS.filter(shortcut => shortcut.visible)
  }

  getSearchResults(value) {
    const { results, didFetch } = this.state;

    this.setState({
      isLoading: results && !didFetch
    }, () => {
      SearchController
        .search(value)
        .then(response => {
          this.setState({
            didFetch: true,
            isLoading: false,
            results: response
          });
        })
        .catch(error => {
          console.error(error)
          this.setState({
            didFetch: true,
            isLoading: false,
            results: []
          })
        })
    });
  }

  resetForm() {
    this.setState({
      results: null,
      didFetch: false,
      isLoading: false,
      value: '',
      shortcuts: [],
    });
  }

  onSearchInputChange(value: string) {
    if (value.length === 0) {
      this.resetForm()
      return;
    }
    else if (value.length > 1) {
      const shortcuts = this.shortcutFuzzySearch.search(value).map(result => result.item)
      this.setState({ shortcuts })

      this.getSearchResults(value);
    }

    this.setState({ value });
  }

  onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      this.resetForm()
    }
  }

  onItemClick(item) {
    const { history } = this.props

    switch (item.object) {
      case 'shortcut':
        history.push(item.link)
        break
      case 'person':
      case 'company':
      case 'contact':
        history.push(RouteHelper.process(ERoute.PATH_CONTACT, { id: item.id }))
        break
      case 'project':
        history.push(RouteHelper.process(ERoute.PATH_PROJECT, { id: item.id }))
        break
      case 'order_form':
      case 'delivery_note':
      case 'quotation':
      case 'invoice':
      case 'credit_note':
        history.push(RouteHelper.process(ERoute.PATH_INVOICE, { id: item.id }))
        break
      case 'expense':
        history.push(RouteHelper.process(ERoute.PATH_EXPENSES, { id: item.id }))
        break
      case 'product':
        history.push(RouteHelper.process(ERoute.PATH_PRODUCTS, { id: item.id }))
        break
      case 'transaction':
        history.push(RouteHelper.process(ERoute.PATH_TRANSACTION, { id: item.id }))
        break
      case 'payment':
        history.push(RouteHelper.process(ERoute.PATH_BANK_PAYMENTS, { id: item.id }))
        break
      case 'deal':
        history.push(RouteHelper.process(ERoute.PATH_DEAL, { id: item.id }))
        break
      case 'form':
        history.push(RouteHelper.process(ERoute.PATH_FORM, { id: item.id }))
        break
      case 'proposal':
        history.push(RouteHelper.process(ERoute.PATH_PROPOSAL, { id: item.id }))
        break
      case 'contract':
        history.push(RouteHelper.process(ERoute.PATH_CONTRACT, { id: item.id }))
        break
      case 'task':
        this.props.showTaskModal({ task: { id: item.id } })
        break
      case 'sequence':
        history.push(RouteHelper.process(ERoute.PATH_SEQUENCE, { id: item.id }))
        break
      default:
        console.warn(`Unknown item click of object -> ${item.object}`);
    }

    this.resetForm()
  }

  renderResults() {
    const { results } = this.state

    return (
      <>
        {results.map((result) => {
          switch (result.object) {
            case 'order_form':
            case 'delivery_note':
            case 'quotation':
            case 'invoice':
            case 'credit_note':
              return this.renderInvoice(result)
            case 'recurring_invoice':
              return this.renderRecurringInvoice(result)
            case 'company':
            case 'person':
            case 'contact': return this.renderContact(result)
            case 'project': return this.renderProject(result)
            case 'expense': return this.renderExpense(result)
            case 'product': return this.renderProduct(result)
            case 'transaction': return this.renderTransaction(result)
            case 'payment': return this.renderPayment(result)
            case 'deal': return this.renderDeal(result)
            case 'form': return this.renderForm(result)
            case 'proposal': return this.renderProposal(result)
            case 'contract': return this.renderContract(result)
            case 'task': return this.renderTask(result)
            case 'sequence': return this.renderSequence(result)
            default:
              console.error(`Unknown search object: ${result.object}`, result)
              return null
          }
        })}
      </>
    )
  }

  renderInvoice(search_invoice) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_invoice.id}
        value={`${search_invoice.object}-${search_invoice.id}`}
        type={search_invoice.object}
        title={Highlighter.highlight(search_invoice.number, value)}
        onSelect={() => this.onItemClick(search_invoice)}
      />
    )
  }

  renderRecurringInvoice(search_invoice) {
    const { t } = this.props

    return (
      <SearchItem
        key={search_invoice.id}
        value={`${search_invoice.object}-${search_invoice.id}`}
        type={search_invoice.object}
        title={t('Search::Recurring invoice')}
        onSelect={() => this.onItemClick(search_invoice)}
      />
    )
  }

  renderContact(search_contact) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_contact.id}
        value={`${search_contact.object}-${search_contact.id}`}
        type={search_contact.object}
        title={Highlighter.highlight(`${search_contact.name} ${search_contact.alias ? `(${search_contact.alias})` : ''}`, value)}
        onSelect={() => this.onItemClick(search_contact)}
      />
    )
  }

  renderProject(search_project) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_project.id}
        value={`${search_project.object}-${search_project.id}`}
        type={search_project.object}
        title={Highlighter.highlight(search_project.name, value)}
        onSelect={() => this.onItemClick(search_project)}
      />
    )
  }

  renderExpense(search_expense) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_expense.id}
        value={`${search_expense.object}-${search_expense.id}`}
        type={search_expense.object}
        title={Highlighter.highlight(search_expense.name, value)}
        onSelect={() => this.onItemClick(search_expense)}
      />
    )
  }

  renderProduct(search_product) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_product.id}
        value={`${search_product.object}-${search_product.id}`}
        type={search_product.object}
        title={Highlighter.highlight(search_product.name, value)}
        onSelect={() => this.onItemClick(search_product)}
      />
    )
  }

  renderTransaction(search_transaction) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_transaction.id}
        value={`${search_transaction.object}-${search_transaction.id}`}
        type={search_transaction.object}
        title={Highlighter.highlight(search_transaction.counterpart_name, value)}
        onSelect={() => this.onItemClick(search_transaction)}
      />
    )
  }

  renderPayment(search_payment) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_payment.id}
        value={`${search_payment.object}-${search_payment.id}`}
        type={search_payment.object}
        title={Highlighter.highlight(search_payment.name, value)}
        onSelect={() => this.onItemClick(search_payment)}
      />
    )
  }

  renderDeal(search_deal) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_deal.id}
        value={`${search_deal.object}-${search_deal.id}`}
        type={search_deal.object}
        title={Highlighter.highlight(search_deal.name, value)}
        onSelect={() => this.onItemClick(search_deal)}
      />
    )
  }

  renderForm(search_form) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_form.id}
        value={`${search_form.object}-${search_form.id}`}
        type={search_form.object}
        title={Highlighter.highlight(search_form.name, value)}
        onSelect={() => this.onItemClick(search_form)}
      />
    )
  }

  renderProposal(search_proposal) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_proposal.id}
        value={`${search_proposal.object}-${search_proposal.id}`}
        type={search_proposal.object}
        title={Highlighter.highlight(search_proposal.name, value)}
        onSelect={() => this.onItemClick(search_proposal)}
      />
    )
  }

  renderContract(search_contract) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_contract.id}
        value={`${search_contract.object}-${search_contract.id}`}
        type={search_contract.object}
        title={Highlighter.highlight(search_contract.name, value)}
        onSelect={() => this.onItemClick(search_contract)}
      />
    )
  }

  renderTask(search_task) {
    const { value } = this.state

    return (
      <SearchItem
        key={search_task.id}
        value={`${search_task.object}-${search_task.id}`}
        type={search_task.object}
        title={Highlighter.highlight(search_task.name, value)}
        onSelect={() => this.onItemClick(search_task)}
      />
    )
  }

  renderSequence(searchSequence) {
    const { value } = this.state

    return (
      <SearchItem
        key={searchSequence.id}
        value={`${searchSequence.object}-${searchSequence.id}`}
        type={searchSequence.object}
        title={Highlighter.highlight(searchSequence.name, value)}
        onSelect={() => this.onItemClick(searchSequence)}
      />
    )
  }

  render() {
    const { centered, t } = this.props
    const { value, shortcuts, results, isLoading } = this.state
    const showDropdown = shortcuts.length > 0 || (results && results.length > 0)

    return (
      <Wrapper ref={this.container}>
        <Command
          shouldFilter={false}
          loop
          className="relative w-full min-w-65 overflow-visible bg-transparent **:data-[slot=command-input-wrapper]:max-h-[35px]! **:data-[slot=command-input-wrapper]:rounded-md **:data-[slot=command-input-wrapper]:border **:data-[slot=command-input-wrapper]:border-input **:data-[slot=command-input-wrapper]:bg-background"
        >
          <CommandInput
            autoCapitalize='off'
            value={value}
            placeholder={t('Search::Search...')}
            onValueChange={this.onSearchInputChange}
            onKeyDown={this.onInputKeyDown}
            className="h-full! border-0! rounded-none! py-0! focus:border-0! focus:shadow-none!"
          />

          {isLoading && <SearchLoader />}

          {showDropdown && (
            <ResultsPositioner className={centered ? 'is-centered' : ''}>
              <CommandList className="max-h-100 rounded-md border border-input bg-popover shadow-md">
                {shortcuts.length > 0 && (
                  <CommandGroup
                    heading={t('Search::Shortcuts')}
                    className="**:[[cmdk-group-heading]]:font-bold **:[[cmdk-group-heading]]:uppercase"
                  >
                    {shortcuts.map((shortcut, index) => {
                      const ShortcutIcon = SHORTCUT_ICONS[shortcut.icon]

                      return (
                        <SearchItem
                          key={`${shortcut.link}-${index}`}
                          value={`shortcut-${index}`}
                          icon={ShortcutIcon}
                          title={Highlighter.highlight(shortcut.label, value)}
                          onSelect={() => this.onItemClick(shortcut)}
                        />
                      )
                    })}
                  </CommandGroup>
                )}

                {results && results.length > 0 && (
                  <CommandGroup>
                    {this.renderResults()}
                  </CommandGroup>
                )}
              </CommandList>
            </ResultsPositioner>
          )}
        </Command>
      </Wrapper>
    )
  }
}

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showTaskModal: typeof showTaskModal
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showTaskModal: (options) => dispatch(showTaskModal(options))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(Search)))
